import { useEffect, useState, type ReactNode } from 'react'

import { Link } from '@tanstack/react-router'

import { ArrowLeft, ChevronDown } from 'lucide-react'

import type { Product } from '#bv/data/products'

import { SERVER_BOOST_3M_PRODUCT_ID, SERVER_BOOST_PRODUCT_ID } from '#bv/data/products'

import { StandardProductPanel } from '#bv/components/StandardProductPanel'
import { StandardProductGallery } from '#bv/components/StandardProductGallery'
import { BundleProductPanel } from '#bv/components/BundleProductPanel'
import { ProductPurchaseActions } from '#bv/components/ProductPurchaseActions'

import { SmmProductPurchase } from '#bv/components/SmmProductPurchase'

import { RobuxProductPurchase } from '#bv/components/RobuxProductPurchase'

import { ServerBoostProductPurchase } from '#bv/components/ServerBoostProductPurchase'

import { isSmmProduct } from '#bv/lib/smmProduct'

import { isRobuxProduct } from '#bv/lib/robuxProduct'

import { isServerBoostProduct } from '#bv/lib/serverBoostProduct'

import { ProductCard } from '#bv/components/ProductCard'

import { productImageUrl } from '#bv/lib/productImageUrl'

import { StarRatingPink } from '#bv/components/FeatureIcons'
import { useProductStock } from '#bv/lib/useProductStock'

import { categories, type Product as ProductType } from '#bv/data/products'

import {

  cartLineDisplayName,

  getProductFamilyContext,

  getProductFamilyTierLabel,

  PRODUCT_FAMILY_HUB_IDS,

} from '#bv/data/productFamilies'

import { FamilyTierLinks } from '#bv/components/FamilyTierLinks'

import { productCardBlurb } from '#bv/lib/productCardCopy'



const SERVER_BOOST_TABS = [

  { id: SERVER_BOOST_PRODUCT_ID, label: '1 month' },

  { id: SERVER_BOOST_3M_PRODUCT_ID, label: '3 months' },

] as const



const BADGE_LABELS: Record<NonNullable<ProductType['badge']>, string> = {

  bestseller: 'Best seller',

  popular: 'Popular',

  new: 'New',

  limited: 'Limited',

  recommended: 'Recommended',

  elite: 'Elite',

}



type Props = {

  product: ProductType

  catalog: ProductType[]

  highlightTierId?: number

}



function categoryLabel(product: ProductType): string {

  return categories.find((c) => c.id === product.category)?.label ?? product.category

}



function hasSharedAccountListing(product: ProductType): boolean {

  return /\bshared access\b/i.test(product.name)

}



export function ProductCheckoutPage({ product, catalog, highlightTierId }: Props) {

  const [faqOpen, setFaqOpen] = useState(false)

  const [selectedVariant, setSelectedVariant] = useState(0)

  const { inStock } = useProductStock()

  const isBundle = Boolean(product.bundlePicker)

  const familyCtx = !isBundle ? getProductFamilyContext(product.id, catalog) : null

  const familyTierLabel = familyCtx ? getProductFamilyTierLabel(product.id, catalog) : null

  const checkoutName = cartLineDisplayName(product.id, product.name, catalog)

  const imgSrc = productImageUrl(product.image)

  const ratingText = product.rating.toFixed(1)

  const brand = categoryLabel(product)

  const tagline = productCardBlurb(product) ?? product.shortDescription



  const smmProduct = isSmmProduct(product) ? product : null

  const robuxProduct = isRobuxProduct(product) ? product : null

  const serverBoostProduct = isServerBoostProduct(product) ? product : null

  const serverBoostListing =

    isBundle && product.id === PRODUCT_FAMILY_HUB_IDS.discord

      ? (catalog.find((p) => isServerBoostProduct(p)) ?? null)

      : null



  const variantPrice = product.variants

    ? product.price + (product.variants[selectedVariant]?.priceModifier ?? 0)

    : product.price



  const related = catalog

    .filter(

      (p) =>

        p.category === product.category &&

        p.id !== product.id &&

        !p.hideFromShop &&

        p.id !== familyCtx?.hubId,

    )

    .slice(0, 4)



  useEffect(() => {

    if (!isBundle || !highlightTierId) return

    const timer = window.setTimeout(() => {

      const targetId =

        highlightTierId === SERVER_BOOST_PRODUCT_ID || highlightTierId === SERVER_BOOST_3M_PRODUCT_ID

          ? 'product-purchase'

          : `tier-${highlightTierId}`

      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })

    }, 120)

    return () => window.clearTimeout(timer)

  }, [isBundle, highlightTierId, product.id])



  const checkoutLabel = serverBoostProduct
    ? 'Select quantity'
    : smmProduct
      ? 'Choose package'
      : robuxProduct
        ? 'PayPal F&F or crypto'
        : null

  const usesConfiguratorCheckout = Boolean(serverBoostProduct || smmProduct || robuxProduct)

  const gallerySalePct =
    !usesConfiguratorCheckout &&
    !isBundle &&
    product.compareAtPrice != null &&
    product.compareAtPrice > variantPrice + 0.004
      ? Math.round((1 - variantPrice / product.compareAtPrice) * 100)
      : null

  const checkoutExtras = (
    <>
      {product.faqs && product.faqs.length > 0 ? (
        <div className="sb-checkout-faq">
          <button
            type="button"
            className="sb-checkout-faq__trigger"
            aria-expanded={faqOpen}
            onClick={() => setFaqOpen((v) => !v)}
          >
            <span>Questions</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${faqOpen ? 'rotate-180' : ''}`} aria-hidden />
          </button>
          {faqOpen ? (
            <dl className="sb-checkout-faq__body">
              {product.faqs.map((faq) => (
                <div key={faq.q}>
                  <dt>{faq.q}</dt>
                  <dd>{faq.a}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      ) : null}
      {familyCtx ? (
        <div className="mt-4 border-t border-white/[0.06] pt-4">
          <FamilyTierLinks
            familyName={familyCtx.familyName}
            hubId={familyCtx.hubId}
            pickerTitle={familyCtx.pickerTitle}
            tiers={familyCtx.tiers}
            catalog={catalog}
            currentProductId={product.id}
            inStock={inStock}
            embedded
          />
        </div>
      ) : null}
      {related.length > 0 && !familyCtx && !isBundle ? (
        <div className="mt-6 border-t border-white/[0.06] pt-6">
          <p className="m-0 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-[#5a6d8a]">You may also like</p>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} variant="vault" />
            ))}
          </div>
        </div>
      ) : null}
    </>
  )

  if (!usesConfiguratorCheckout) {
    return (
      <div className="sb-checkout-page" style={{ background: 'var(--black)', color: 'var(--text)' }}>
        <div className="sb-checkout-page__glow" aria-hidden />
        <div className="sb-checkout-page__inner sb-checkout-page__inner--wide">
          <Link to="/shop" className="sb-checkout-back">
            <ArrowLeft className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            Shop
          </Link>
          <div className="pc-pdp-layout">
            <StandardProductGallery
              product={product}
              imgSrc={imgSrc}
              selectedVariant={selectedVariant}
              onVariantChange={setSelectedVariant}
              salePct={gallerySalePct}
            />
            {isBundle && product.bundlePicker ? (
              <BundleProductPanel
                product={product as ProductType & { bundlePicker: NonNullable<ProductType['bundlePicker']> }}
                catalogBrand={brand}
                catalog={catalog}
                highlightTierId={highlightTierId}
                inStock={inStock}
                serverBoostListing={serverBoostListing}
              />
            ) : (
              <StandardProductPanel
                product={product}
                catalogBrand={brand}
                checkoutName={checkoutName}
                unitPrice={variantPrice}
                familyCtx={familyCtx}
                familyTierLabel={familyTierLabel}
              />
            )}
          </div>
          {checkoutExtras}
        </div>
        {!isBundle ? (
          <div className="sticky-mobile-cta lg:hidden">
            <div className="mx-auto max-w-lg px-4">
              <ProductPurchaseActions
                productId={product.id}
                name={checkoutName}
                unitPrice={variantPrice}
                compact
              />
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  let tabs: ReactNode = null

  if (serverBoostProduct) {

    tabs = (

      <div className="sb-checkout-tabs" role="tablist" aria-label="Boost duration">

        {SERVER_BOOST_TABS.map((tab) => {

          const active = product.id === tab.id

          return (

            <Link

              key={tab.id}

              to="/products/$productId"

              params={{ productId: String(tab.id) }}

              role="tab"

              aria-selected={active}

              className={`sb-checkout-tab ${active ? 'sb-checkout-tab--active' : ''}`}

            >

              {tab.label}

            </Link>

          )

        })}

      </div>

    )

  }



  const purchasePanel = (

    <div id="product-purchase" className="pc-checkout-purchase scroll-mt-24">

      {serverBoostProduct ? (

        <ServerBoostProductPurchase product={serverBoostProduct} variant="sheet" />

      ) : smmProduct ? (

        <SmmProductPurchase product={smmProduct} />

      ) : robuxProduct ? (

        <RobuxProductPurchase product={robuxProduct} embedded />

      ) : null}

    </div>

  )



  return (

    <div className="sb-checkout-page" style={{ background: 'var(--black)', color: 'var(--text)' }}>

      <div className="sb-checkout-page__glow" aria-hidden />



      <div className="sb-checkout-page__inner">

        <Link to="/shop" className="sb-checkout-back">

          <ArrowLeft className="h-4 w-4" strokeWidth={2.25} aria-hidden />

          Shop

        </Link>



        {tabs}



        <div className="sb-checkout-layout">

          <div className="sb-checkout-visual">

            <div className="sb-checkout-hero">

              <div className="sb-checkout-hero__frame">

                <div className="sb-checkout-hero__inner">

                  <img src={imgSrc} alt={product.name} className="sb-checkout-hero__img" loading="eager" decoding="async" />

                  {product.badge ? (

                    <span className="sb-checkout-hero__badge">{BADGE_LABELS[product.badge]}</span>

                  ) : null}

                </div>

              </div>

            </div>



            <div className="sb-checkout-meta">

              <p className="sb-checkout-meta__kicker">

                {brand}

                {familyTierLabel ? ` · ${familyTierLabel}` : ''}

              </p>

              <h1 className="sb-checkout-meta__title font-display chrome-text">{product.name}</h1>

              <p className="sb-checkout-meta__rating">
                <StarRatingPink rating={product.rating} size={15} />
                <span className="font-bold tabular-nums text-[#00bfff]">{ratingText}</span>
                <span className="text-[#7a6574]">({product.reviewCount.toLocaleString()} reviews)</span>
              </p>

              {tagline ? (
                <p className="sb-checkout-meta__blurb">{tagline}</p>
              ) : null}

              {familyCtx ? (

                <Link

                  to="/products/$productId"

                  params={{ productId: String(familyCtx.hubId) }}

                  search={{ tier: product.id }}

                  className="mt-2 inline-block text-xs font-semibold text-[#93c5fd] hover:text-[#7ec8ff]"

                >

                  Compare all {familyCtx.familyName} tiers →

                </Link>

              ) : null}

              {hasSharedAccountListing(product) ? (

                <p className="mt-3 rounded-lg border border-amber-500/25 bg-amber-950/25 px-3 py-2 text-xs leading-relaxed text-[#8aa0c4]">

                  <span className="font-semibold text-[#d4e8ff]">Shared access:</span> access duration is not

                  guaranteed. See FAQ for details.

                </p>

              ) : null}

            </div>

          </div>



          <article className="sb-checkout-card">

            {checkoutLabel ? (
              <p className="sb-checkout-card__label">{checkoutLabel}</p>
            ) : null}

            {purchasePanel}

          </article>

        </div>



        {checkoutExtras}

      </div>

    </div>

  )

}

